import { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { LoadingOverlay, Modal } from "@/components";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { CustomColumnTypes, PriorityFilterStatus, UpdateType } from "@/enum/list.enum";
import { InputField } from "@/components/InputField/InputField";
import { useUpdateListColumnsMutation } from "@/pages/Private/redux/list/list.api";

import textIcon from "@/assets/icons/textIcon.svg?url";
import numbersIcon from "@/assets/icons/numbersIcon.svg?url";
import linkIcon from "@/assets/icons/linkIcon.svg?url";
import aiIcon from "@/assets/icons/aiIcon.svg?url";
import checkboxIcon from "@/assets/icons/checkboxIcon.svg?url";
import diceIcon from "@/assets/icons/diceIcon.svg?url";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { ButtonColor } from "@/components/Button/types";

import { List, CustomColumn, CustomColumnSchema } from "@/pages/Private/pages/List/schema/list";

import { ColumnConfig } from "../schema/columnConfig";
import { AiModal } from "./CustomColumns/Modals/AIModal";
import { DepartmentModal } from "./CustomColumns/Modals/DepartmentModal";
import { GoogleNewsModal } from "./CustomColumns/Modals/GoogleNewsModal";
import { JobOfferModal } from "./CustomColumns/Modals/JobOfferModal";
import { LinkedinPostModal } from "./CustomColumns/Modals/LinkedinPostModal";
import { TechnologyModal } from "./CustomColumns/Modals/TechnologyModal";
import { LinkedinPostEngagementModal } from "./CustomColumns/Modals/LinkedinPostEngagementModal";
import { RandomValueModal } from "./CustomColumns/Modals/RandomValueModal";
import { TextValueModal } from "./CustomColumns/Modals/TextValueModal";
import { CustomColumnApplyFilters } from "./CustomColumns/customColumnApplyFilters";

export interface EditCustomColumnModalProps {
	setOpen: (value: boolean) => void;
	open: boolean;
	listId: number;
	columnId: number;
	list?: List;
	column: ColumnConfig;
	filters?: CustomColumnApplyFilters;
	selectedProspectIds?: number[];
	priorityFilterStatus?: PriorityFilterStatus[];
}

export const EditCustomColumnModal: FunctionComponent<EditCustomColumnModalProps> = ({
	open,
	setOpen,
	listId,
	columnId,
	column,
	list,
	filters,
	selectedProspectIds,
	priorityFilterStatus,
}) => {
	const { t } = useTranslation();

	const ts = useCallback((key: string) => t(`inbox.${key}`), [t]);
	const [columnType, setColumnType] = useState<CustomColumnTypes>(column.type);

	const [showConfirm, setShowConfirm] = useState<boolean>(false);

	const options = useMemo(
		() => [
			{
				title: ts(CustomColumnTypes.TEXT),
				id: CustomColumnTypes.TEXT,
				icon: <img alt="textIcon" className="mr-1" src={textIcon} />,
			},
			{
				title: ts(CustomColumnTypes.NUMBER),
				id: CustomColumnTypes.NUMBER,
				icon: <img alt="numbersIcon" className="mr-1" src={numbersIcon} />,
			},
			{
				title: ts(CustomColumnTypes.LINK),
				id: CustomColumnTypes.LINK,
				icon: <img alt="linkIcon" className="mr-1" src={linkIcon} />,
			},
			{
				title: ts(CustomColumnTypes.BOOLEAN),
				id: CustomColumnTypes.BOOLEAN,
				icon: <img alt="linkIcon" className="mr-1" src={checkboxIcon} />,
			},
			{
				title: ts(CustomColumnTypes.AI_PROMPT),
				id: CustomColumnTypes.AI_PROMPT,
				icon: <img alt="linkIcon" className="mr-1" src={aiIcon} />,
			},
			{
				title: ts(CustomColumnTypes.RANDOM_VALUE),
				id: CustomColumnTypes.RANDOM_VALUE,
				icon: <img alt="linkIcon" className="mr-1" src={diceIcon} />,
			},
		],
		[ts]
	);

	const [update, { isLoading: updateLoading, error }] = useUpdateListColumnsMutation();

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<CustomColumn>({
		defaultValues: {
			customColumnName: column?.name,
			customColumnType: column?.type,
			value: column?.config,
		},
		resolver: zodResolver(CustomColumnSchema),
	});

	const onSubmit = async (values: CustomColumn) => {
		try {
			await update({
				updateType: UpdateType.CUSTOM_COLUMN,
				id: listId,
				columnId,
				...values,
				priorityFilterStatus: priorityFilterStatus ?? undefined,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	return !column ? (
		<LoadingOverlay />
	) : column ? (
		<Modal
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={updateLoading}
			isOpened={open}
			overflow={true}
			size="xs"
			submitButtonText={t("basics.confirm")}
			subtitle="Change the name and Config of your column"
			title="Edit Column"
		>
			<div className="flex flex-col space-y-4">
				<InputField
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("customColumnName", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Column Name"
					name={"name"}
					placeholder="Enter column name"
					value={getValues("customColumnName") || column?.name || ""}
				/>

				<Dropdown
					data={options}
					disabled={true}
					floating={true}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id) {
							setValue("customColumnType", value.id as CustomColumnTypes);
							setColumnType(value.id as CustomColumnTypes);
						}
					}}
					label="Custom Column Type"
					value={options.find((option: AutoCompleteItem) => option.id === column?.type)}
				/>

				{columnType === CustomColumnTypes.RANDOM_VALUE && (
					<RandomValueModal
						column={column}
						columnId={columnId}
						customColumnName={getValues("customColumnName")}
						listId={listId}
						open={true}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.TEXT && (
					<TextValueModal
						column={column}
						columnId={columnId}
						customColumnName={getValues("customColumnName")}
						filters={filters}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.AI_PROMPT && (
					<AiModal
						column={column}
						columnId={columnId}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.JOB_OFFER && (
					<JobOfferModal
						column={column}
						columnId={columnId}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.LINKEDIN_POST && (
					<LinkedinPostModal
						column={column}
						columnId={columnId}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.LINKEDIN_POST_ENGAGEMENT && (
					<LinkedinPostEngagementModal
						column={column}
						columnId={columnId}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.DEPARTMENT_SIZE && (
					<DepartmentModal
						column={column}
						columnId={columnId}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.GOOGLE_NEWS && (
					<GoogleNewsModal
						column={column}
						columnId={columnId}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.TECHNOLOGY && (
					<TechnologyModal
						column={column}
						columnId={columnId}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</div>
			{showConfirm && (
				<Modal
					handleClose={() => setShowConfirm(false)}
					handleSave={async () => {
						setShowConfirm(false);
						setOpen(false);
					}}
					isOpened={showConfirm}
					size="xs"
					submitButtonColor={ButtonColor.ACTION}
					submitButtonText={t("basics.confirm")}
					title={t("basics.confirm")}
				>
					<>{t("basics.confirmationClose")} </>
				</Modal>
			)}
		</Modal>
	) : (
		<></>
	);
};
